import { Link, useLocation } from 'react-router-dom'
import { useWikiNav } from '@/components/layout/wikiNavContext'
import { CHANGELOG_CATEGORY_SLUG, buildPatchTimeline } from '@/utils/changelog'
import type { WikiPage } from '@/types/wiki'

export default function Sidebar() {
  const { categories, pages, loading, error, refresh, mobileNavOpen, setMobileNavOpen } = useWikiNav()
  const location = useLocation()

  const uncategorized = pages.filter(p => !p.category_id)
  const allGroups = categories
    .map(category => ({
      category,
      categoryPages: pages.filter(p => p.category_id === category.id),
    }))
    .filter(group => group.categoryPages.length > 0)

  // The changelog category collapses into a single Changelog entry, shown right
  // after the Getting Started group instead of at its own category position.
  const changelogGroup = allGroups.find(g => g.category.slug === CHANGELOG_CATEGORY_SLUG)
  const categorizedGroups = allGroups.filter(g => g.category.slug !== CHANGELOG_CATEGORY_SLUG)
  let changelogAfterIndex = categorizedGroups.findIndex(g => g.category.slug === 'getting-started')
  if (changelogAfterIndex < 0 && categorizedGroups.length > 0) changelogAfterIndex = 0

  const navContent = (
    <nav className="space-y-0 lg:pr-4">
      {/* Uncategorized / top-level pages */}
      {uncategorized.length > 0 && (
        <div className="mb-1">
          {uncategorized.map(page => (
            <NavItem key={page.id} page={page} currentPath={location.pathname} />
          ))}
        </div>
      )}

      {/* Changelog entry with no Getting Started group to attach to */}
      {changelogGroup && categorizedGroups.length === 0 && (
        <PatchNotesNavItem pages={changelogGroup.categoryPages} currentPath={location.pathname} />
      )}

      {/* Category groups */}
      {categorizedGroups.map(({ category, categoryPages }, index) => {
        const needsTopGap = index > 0 || uncategorized.length > 0
        return (
          <div key={category.id} className="mb-0">
            {needsTopGap && (
              <div className="mx-3 mt-5 mb-0 border-t border-calamity-border-dim/50" />
            )}
            <h4 className={`ecosystem-section-label px-3 mb-1.5 ${needsTopGap ? 'mt-3' : 'mt-0'}`}>
              {category.name}
            </h4>
            {categoryPages.map(page => (
              <NavItem key={page.id} page={page} currentPath={location.pathname} />
            ))}
            {changelogGroup && index === changelogAfterIndex && (
              <PatchNotesNavItem pages={changelogGroup.categoryPages} currentPath={location.pathname} />
            )}
          </div>
        )
      })}
    </nav>
  )

  return (
    <>
      {/* Mobile overlay */}
      <div
        className={`
          fixed inset-0 z-[60] bg-black/60 lg:hidden
          transition-opacity duration-300 motion-reduce:transition-none
          ${mobileNavOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}
        `}
        onClick={() => setMobileNavOpen(false)}
      />

      {/* Sidebar */}
      <aside
        className={`
          shrink-0 overflow-y-auto
          fixed inset-y-0 left-0 z-[70] w-72 bg-calamity-menu-surface p-4 pt-16 border-r border-calamity-border shadow-sidebar
          transform transition-transform duration-300 ease-out motion-reduce:transition-none
          ${mobileNavOpen ? 'translate-x-0' : '-translate-x-full pointer-events-none'}

          lg:relative lg:inset-auto lg:z-auto lg:w-56
          lg:sticky lg:top-[var(--wiki-sticky-top)] lg:self-start lg:max-h-[var(--wiki-sticky-max-h)]
          lg:rounded-none lg:border-0 lg:bg-transparent lg:p-0 lg:pt-0 lg:shadow-none
          lg:translate-x-0 lg:pointer-events-auto lg:transition-none lg:transform-none
        `}
      >
        {mobileNavOpen && (
          <button
            onClick={() => setMobileNavOpen(false)}
            className="ecosystem-control-surface absolute top-4 right-4 flex h-10 w-10 items-center justify-center lg:hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#b783ff]/45 focus-visible:ring-offset-2 focus-visible:ring-offset-calamity-menu-surface"
            aria-label="Close navigation"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
        <div className="overflow-hidden lg:overflow-visible">
          {loading ? (
            <div className="animate-pulse space-y-3 px-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-10 rounded-[10px] bg-white/5" />
              ))}
            </div>
          ) : error ? (
            <div className="text-sm text-calamity-text-dim px-3">
              <div className="mb-3">Failed to load navigation.</div>
              <button
                className="ecosystem-control-surface inline-flex items-center justify-center text-xs px-3 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#b783ff]/45 focus-visible:ring-offset-2 focus-visible:ring-offset-calamity-menu-surface"
                onClick={refresh}
                type="button"
              >
                Retry
              </button>
            </div>
          ) : navContent}
        </div>
      </aside>
    </>
  )
}

function PatchNotesNavItem({ pages, currentPath }: { pages: WikiPage[]; currentPath: string }) {
  const latest = buildPatchTimeline(pages)[0] ?? null
  // Keep the hub link lit while reading any individual patch page too.
  const isActive = currentPath === '/changelog'
    || pages.some(page => currentPath === `/${page.slug}`)

  return (
    <Link
      to="/changelog"
      className={`
        ecosystem-sidebar-link mb-1 flex items-center gap-2.5 px-3 py-2.5 text-[15px] font-medium leading-5 transition-colors last:mb-0
        ${isActive
          ? 'ecosystem-sidebar-link-active'
          : 'ecosystem-sidebar-link-inactive'
        }
      `}
    >
      <span className="text-base leading-none" aria-hidden="true">{'🧾'}</span>
      <span className="truncate">Changelog</span>
      {latest?.version && (
        <span className="ml-auto shrink-0 rounded-full border border-[#ffb04a]/40 bg-[#ffb04a]/10 px-2 py-0.5 text-[10px] font-bold text-[#ffb04a]">
          {latest.version}
        </span>
      )}
    </Link>
  )
}

function NavItem({ page, currentPath }: { page: WikiPage; currentPath: string }) {
  const isActive = currentPath === `/${page.slug}`
  return (
    <Link
      to={`/${page.slug}`}
      className={`
        ecosystem-sidebar-link mb-1 flex items-center gap-2.5 px-3 py-2.5 text-[15px] font-medium leading-5 transition-colors last:mb-0
        ${isActive
          ? 'ecosystem-sidebar-link-active'
          : 'ecosystem-sidebar-link-inactive'
        }
      `}
    >
      <span className="text-base leading-none">{page.icon || '\uD83D\uDCC4'}</span>
      <span className="truncate">{page.title}</span>
    </Link>
  )
}
